/**
 * Errands — Ellie signs in to a site the family already uses, does one job,
 * files what she finds and closes the session (docs/features/ERRANDS_SPEC.md).
 *
 * The card, the approval sheet and the ask sheet all render the same shape the
 * errands API returns for `read` and `list`, so a chat card and a re-read never
 * disagree.
 */
import { apiErrandsPost, apiGet } from './apiClient';
import { decryptCredential, type DecryptedCredential } from './vaultCrypto';
import { openProjectVault } from './vaultUnlock';
import type { SurfacePointer } from './streamChat';

export type ErrandPhase = 'offer' | 'awaiting_approval' | 'running' | 'waiting_family' | 'done' | 'failed' | 'stopped' | 'call_offer';
export type ErrandKind = 'read' | 'submission' | 'purchase' | 'watch';
export type StepState = 'done' | 'running' | 'waiting' | 'blocked';
export type ChipTone = 'ok' | 'warn' | 'neutral';
export type ScheduleEvery = 'week' | 'month';

export interface ErrandAsk {
  id: string;
  /** cvv: a shared card's security code, typed into the checkout and never stored. */
  type: 'code' | 'cvv' | 'captcha' | 'consent' | 'approval' | 'choice' | 'purchase';
  prompt: string;
  detail?: string | null;
  choices?: string[];
  expires_at?: string | null;
}

export interface ErrandData {
  errand_id: string;
  phase: ErrandPhase;
  kind: ErrandKind;
  recipe_key: string;
  title: string;
  summary?: string | null;
  /** phone: the site blocks online sign-in, so this errand is a call. */
  channel?: 'browser' | 'phone' | 'partner';
  /** An errand on a site the member named: it can run without a login (public pages only). */
  login_optional?: boolean;
  /** Runs in their own Chrome (Eleplan for Chrome), signed in already: Allow starts it, no login or Face ID. */
  in_browser?: boolean;
  /** logo_domain: an organization domain for OrgLogo (our logo store); the letter badge is the fallback. */
  site: { name: string; short: string; color: string; logo_domain?: string | null };
  chips?: { label: string; tone: ChipTone }[];
  steps?: { label: string; state: StepState }[];
  needs_family?: { step: string; asks: string; label: string }[];
  /** A browser errand can stop for the family (a texted code, a step that needs them); they get this long to answer. */
  stay_nearby?: { answer_within_s: number; asks: string[] } | null;
  missing_vault?: MissingVaultItem[];
  vault_items?: { vault_item_id: number | string; label: string; login_status?: LoginStatus['status']; login_error?: string | null }[];
  ask?: ErrandAsk | null;
  result?: {
    headline: string;
    facts?: { label: string; value: string; emphasis?: boolean }[];
    captures?: { document_id: number | string; label: string; folder?: string | null }[];
    signed_out_at?: string | null;
    /** The site had no card saved: its own page for saving one. */
    save_card_url?: string | null;
    /** A goal errand's answer, as short as it can be said. */
    answer?: string | null;
    /** She looked and it wasn't there, which isn't the same as the run breaking. */
    not_found?: boolean;
    /** The page the answer came from. */
    source?: { url: string } | null;
    /** What she did on the way, in her words. */
    trail?: string[];
    pages?: number;
    took_s?: number;
  } | null;
  call_offer?: { reason: string; places: string[]; script: string } | null;
  /** What would make the next run easier, on the finished card. */
  next_time?: MissingVaultItem | null;
  hands_off_offer?: { caps: { per_order: number; per_month: number }; payment_label?: string | null; swaps?: string | null } | null;
  purchase?: { merchant: string; item_count: number; swaps?: string | null; delivery?: string | null; payment_label?: string | null; total: string } | null;
  /** A finished run that can be set to run again every week or month. */
  schedulable?: boolean;
  /** This run carries a schedule: when it runs next. */
  schedule?: { every: ScheduleEvery; next_run_at: string | null } | null;
  /** The schedule started this run. */
  scheduled?: boolean;
  started_at?: string | null;
  updated_at?: string | null;
}

/** Something the Vault needs before an errand can run, with what to prefill in New Item. */
export interface MissingVaultItem {
  vault_type: string;
  label: string;
  prefill?: { type: string; label?: string; url?: string } | null;
  /** A saved login that failed its last sign-in: Fix opens that Vault item instead of a new one. */
  fix_vault_item_id?: number | string | null;
  /** The button's text when it isn't the usual Add or Fix. */
  action_label?: string;
}

/** Whether Ellie's sign-in works with one saved login (login checks and real runs). */
export interface LoginStatus {
  vault_item_id: number | string;
  status: 'confirmed' | 'failed' | 'checking' | 'unsupported' | 'never';
  checked_at: string | null;
  site: ErrandData['site'] | null;
  error: { text: string } | null;
  screenshot: boolean;
}

/** One errand the plan is ready for, or one or two Vault items away from (`errand_ready` card). */
export interface ErrandReadyItem {
  recipe_key: string;
  title: string;
  summary?: string | null;
  kind: ErrandKind;
  category?: string | null;
  site: ErrandData['site'];
  /** phone: the site blocks online sign-in, so the errand is a call (phone_reason says why). */
  channel?: 'browser' | 'phone' | 'partner';
  phone_reason?: string | null;
  missing_vault: MissingVaultItem[];
}

/** Opens the plan's Vault with New Item prefilled for what an errand is missing, or the failed login to fix. */
export function openVaultPrefill(projectId: number | string, item: MissingVaultItem) {
  // Required here, not at the top: cards import this module, and screens that
  // mock navigation must still be able to load them.
  // eslint-disable-next-line @typescript-eslint/no-require-imports
  const { navigationRef } = require('../navigation/navigationRef');
  if (!navigationRef.isReady()) return false;
  if (item.fix_vault_item_id != null) {
    (navigationRef as any).navigate('Main', { screen: 'Plans', params: { screen: 'ProjectVault', params: { projectId, credentialId: item.fix_vault_item_id } } });
    return true;
  }
  if (!item.prefill) return false;
  const prefill = { ...item.prefill, key: `${item.prefill.type}:${item.prefill.label || ''}:${Date.now()}` };
  (navigationRef as any).navigate('Main', { screen: 'Plans', params: { screen: 'ProjectVault', params: { projectId, prefill } } });
  return true;
}

/** What an errand push adds to the plan chat: Ellie's line and the live card, or why it couldn't open. */
export function errandReturnTurn(errand: ErrandData | null | undefined, projectId: number | string): { content: string; cards?: SurfacePointer[] } {
  if (!errand?.errand_id) return { content: 'I couldn’t open that errand. Check your connection, then tap the notification again.' };
  return {
    // The card below keeps moving, so this line has to stay true as it does.
    content: errand.phase === 'done' ? 'All done. Here’s what I found.' : 'Here’s where this errand stands.',
    cards: [{ target_type: 'errand', target_id: `errand:${errand.errand_id}`, project_id: projectId, label: errand.title, shown: true, open_ask: true, card: { kind: 'errand', data: errand as any } }],
  };
}

export const ACTIVE_PHASES: ErrandPhase[] = ['awaiting_approval', 'running', 'waiting_family'];
export const isActive = (phase?: ErrandPhase | null) => !!phase && ACTIVE_PHASES.includes(phase);

// Relative to config.errandsApi.endpoint, which already ends in /errands.
const errandsPath = (projectId: number | string, accountId: string, cognitoId: string) => `/${projectId}/${accountId}/${cognitoId}`;

export interface ErrandIds { projectId: number | string; accountId: string; cognitoId: string }

/** One errands API action. Resolves the envelope; never throws. */
export async function errandAction(ids: ErrandIds, action: string, body: Record<string, any> = {}): Promise<{ success: boolean; payload?: any; message?: string }> {
  try {
    const res = await apiErrandsPost(errandsPath(ids.projectId, ids.accountId, ids.cognitoId), { action, ...body });
    return res && typeof res === 'object' ? res : { success: false };
  } catch {
    return { success: false, message: 'Could not reach Errands. Check your connection and try again.' };
  }
}

export type SecretsResult =
  | { status: 'ready'; sites: { vault_item_id: number | string; enabled: true; secret: { username?: string; password?: string; otp_secret?: string } }[] }
  | { status: 'needs_recovery' | 'denied' | 'error' | 'missing' };

/**
 * Decrypts only the logins the family switched on, on this device, for one
 * run. The values go straight into the approve request (TLS) and are sealed
 * server side for that run; nothing here keeps them.
 */
export async function loginsForRun(ids: ErrandIds, vaultItemIds: (number | string)[]): Promise<SecretsResult> {
  const unlock = await openProjectVault({ accountId: ids.accountId, projectId: ids.projectId, cognitoId: ids.cognitoId });
  if (unlock.status !== 'ready') return { status: unlock.status === 'foreign_key' ? 'denied' : unlock.status };
  const res = await apiGet(`/credentials/get-all/${ids.projectId}/${ids.accountId}/${ids.cognitoId}`).catch(() => null);
  const rows: any[] = Array.isArray(res?.payload) ? res.payload : Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : [];
  const sites: Extract<SecretsResult, { status: 'ready' }>['sites'] = [];
  for (const id of vaultItemIds) {
    const row = rows.find((r) => String(r.id) === String(id));
    const login = row ? decryptCredential(unlock.vek, row.encrypted_data, row.iv, row.auth_tag) : null;
    if (!login?.username || !login.password) return { status: 'missing' };
    sites.push({ vault_item_id: id, enabled: true, secret: { username: login.username, password: login.password, ...(login.otp_secret ? { otp_secret: login.otp_secret } : {}) } });
  }
  return { status: 'ready', sites };
}

/**
 * Said before an errand starts: the family needs to be reachable, because a
 * live question waits only a few minutes before Ellie saves her progress and stops.
 */
export function stayNearbyText(errand: Pick<ErrandData, 'stay_nearby' | 'site'>) {
  const stay = errand.stay_nearby;
  if (!stay) return null;
  const minutes = Math.max(1, Math.round(stay.answer_within_s / 60));
  return `Stay nearby once it starts. If ${errand.site?.name || 'the site'} sends a code or Ellie needs you, you’ll get a notification and have about ${minutes} minutes to answer, or she saves her progress and stops.`;
}

/** One saved login's delegated copy, as the errands API reports it (never the value). */
export interface DelegationStatus {
  vault_item_id: number;
  delegated: boolean;
  state?: 'active' | 'needs_update' | 'expired' | 'used_up';
  recipe_keys?: string[];
  allowed_domains?: string[];
  max_uses?: number;
  uses?: number;
  expires_at?: string | null;
  granted_at?: string | null;
  recent_uses?: { errand_id: string; recipe_key: string; phase: string; at: string | null }[];
}

/** The `vault_delegation` chat card: Ellie asks; only the family turns it on. */
export interface VaultDelegationData {
  vault_item_id: number;
  label: string;
  credential_type?: string;
  site: ErrandData['site'];
  recipes: { recipe_key: string; title: string }[];
  allowed_domains: string[];
  defaults: { max_uses: number; expires_days: number };
  limits: { max_uses: number; expires_days: number };
  delegation: DelegationStatus | null;
}

export interface DelegationLimits { recipe_keys: string[]; max_uses: number; expires_days: number }

type DelegationResult = { success: boolean; delegation?: DelegationStatus; message?: string };

const DELEGATION_UNLOCK: Record<string, string> = {
  needs_recovery: 'This device can’t open the Vault yet. Open Vault once to restore it, then try again.',
  denied: 'You don’t have access to this plan’s Vault.',
  missing: 'This item is missing what Ellie needs. Update it in the Vault first.',
  error: 'Couldn’t open the Vault. Check your connection and try again.',
};

// What each delegable type sends; the server keeps the same fields. Never a CVV.
const DELEGATED_FIELDS: Record<string, (keyof DecryptedCredential)[]> = {
  login: ['username', 'password', 'otp_secret'],
  card: ['cardNumber', 'expiry', 'cardholderName', 'billingZip'],
  bank: ['accountNumber', 'routingNumber', 'bankName', 'accountType'],
  identity: ['idNumber', 'idType', 'issueDate', 'expiryDate'],
};

/** One saved item's delegable fields, decrypted on this device. */
async function itemForDelegation(ids: ErrandIds, vaultItemId: number | string): Promise<{ status: 'ready'; secret: Record<string, string> } | { status: 'needs_recovery' | 'denied' | 'error' | 'missing' }> {
  const unlock = await openProjectVault({ accountId: ids.accountId, projectId: ids.projectId, cognitoId: ids.cognitoId });
  if (unlock.status !== 'ready') return { status: unlock.status === 'foreign_key' ? 'denied' : unlock.status };
  const res = await apiGet(`/credentials/get-all/${ids.projectId}/${ids.accountId}/${ids.cognitoId}`).catch(() => null);
  const rows: any[] = Array.isArray(res?.payload) ? res.payload : Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : [];
  const row = rows.find((r) => String(r.id) === String(vaultItemId));
  const item = row ? decryptCredential(unlock.vek, row.encrypted_data, row.iv, row.auth_tag) : null;
  const fields = DELEGATED_FIELDS[row?.credential_type || 'login'];
  if (!item || !fields) return { status: 'missing' };
  const secret: Record<string, string> = {};
  for (const f of fields) if (typeof item[f] === 'string' && item[f]) secret[f] = item[f] as string;
  return Object.keys(secret).length ? { status: 'ready', secret } : { status: 'missing' };
}

/**
 * Lets Ellie use one saved item (login, card, bank account, ID) without this
 * device: decrypts it here and sends it once (TLS) to the errands API, which
 * keeps an encrypted copy limited to these errands, uses and days. A card's
 * CVV is never sent. The caller confirms with Face ID first. With `refresh`,
 * re-sends an edited item and keeps the limits.
 */
export async function delegateItem(ids: ErrandIds, vaultItemId: number | string, limits: DelegationLimits | { refresh: true }): Promise<DelegationResult> {
  const item = await itemForDelegation(ids, vaultItemId);
  if (item.status !== 'ready') return { success: false, message: DELEGATION_UNLOCK[item.status] || DELEGATION_UNLOCK.error };
  const res = await errandAction(ids, 'delegate', { vault_item_id: vaultItemId, secret: item.secret, ...limits });
  return { success: res.success, delegation: res.payload?.delegation, message: res.message };
}

/** Deletes the server copy now. Works even when Errands is off. */
export async function revokeDelegation(ids: ErrandIds, vaultItemId: number | string): Promise<{ success: boolean; message?: string }> {
  const res = await errandAction(ids, 'revoke_delegation', { vault_item_id: vaultItemId });
  return { success: res.success, message: res.message };
}

export async function delegationStatus(ids: ErrandIds, vaultItemId: number | string): Promise<DelegationStatus | null> {
  const res = await errandAction(ids, 'delegation_status', { vault_item_ids: [vaultItemId] });
  return res.success && Array.isArray(res.payload?.delegations) ? res.payload.delegations[0] || null : null;
}

/** "On · 3 of 10 uses · until Jan 26", or what stops Ellie using it. */
export function delegationSummary(status: DelegationStatus | null | undefined) {
  if (!status?.delegated) return 'Off. Ellie asks you before each errand.';
  if (status.state === 'needs_update') return 'Needs updating: this login changed since you shared it.';
  if (status.state === 'expired') return 'Expired. Turn it on again to keep using it.';
  if (status.state === 'used_up') return `Used ${status.uses} of ${status.max_uses} times. Turn it on again for more.`;
  const until = status.expires_at ? new Date(status.expires_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '';
  return `On · ${status.uses ?? 0} of ${status.max_uses} uses${until ? ` · until ${until}` : ''}`;
}

/** "Checked 6 pages in 56 seconds", or null for a run that didn't count them. */
export function checkedText(result: Pick<NonNullable<ErrandData['result']>, 'pages' | 'took_s'> | null | undefined) {
  const pages = result?.pages || 0;
  if (pages < 1) return null;
  const seconds = result?.took_s || 0;
  const minutes = Math.round(seconds / 60);
  const took = seconds < 2 ? '' : seconds < 60 ? ` in ${seconds} seconds` : ` in ${minutes === 1 ? '1 minute' : `${minutes} minutes`}`;
  return `Checked ${pages === 1 ? '1 page' : `${pages} pages`}${took}`;
}

export const REPORT_LABEL = 'What Ellie found';

/** What a goal errand reported, written as a sentence rather than a labelled row. */
export function errandReport(result: NonNullable<ErrandData>['result']) {
  return result?.facts?.find((fact) => fact.label === REPORT_LABEL)?.value || null;
}

/** The page an answer came from, https only, shown as "medicare.gov/basics/costs" with no sign-in part. */
export function safeSource(url?: string | null) {
  if (typeof url !== 'string' || !/^https:\/\//i.test(url)) return null;
  const label = url.replace(/^https:\/\//i, '').replace(/^[^/]*@/, '').replace(/^www\./i, '').replace(/\/$/, '');
  return label ? { url, label } : null;
}

/** "9:44 a.m." for the signed-out line. */
export function clockTime(value?: string | null) {
  const date = value ? new Date(value) : null;
  if (!date || !Number.isFinite(date.getTime())) return '';
  const h = date.getHours();
  return `${h % 12 || 12}:${String(date.getMinutes()).padStart(2, '0')} ${h < 12 ? 'a.m.' : 'p.m.'}`;
}

/** One scheduled errand, as the errands API `schedules` action lists it. */
export interface ErrandSchedule {
  errand_id: string;
  recipe_key: string;
  title: string;
  site: ErrandData['site'];
  every: ScheduleEvery;
  next_run_at: string | null;
  vault_item_id: number | null;
  /** Set by this member: only they can cancel it. */
  mine: boolean;
  last: { at: string; outcome: string } | null;
  /** The offer the schedule created that still needs the family's approval. */
  waiting_errand_id: string | null;
}

/** The plan's schedules that use one saved login. Null when they couldn't be read. */
export async function listSchedules(ids: ErrandIds, vaultItemId: number | string): Promise<ErrandSchedule[] | null> {
  const res = await errandAction(ids, 'schedules', { vault_item_id: vaultItemId });
  return res.success && Array.isArray(res.payload?.schedules) ? res.payload.schedules : null;
}

/** "Every week · next Nov 4" */
export function scheduleText(schedule: { every: ScheduleEvery; next_run_at: string | null }) {
  const d = schedule.next_run_at ? new Date(schedule.next_run_at) : null;
  const next = d && Number.isFinite(d.getTime()) ? d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '';
  return `Every ${schedule.every}${next ? ` · next ${next}` : ''}`;
}
